import { FormAutoComplete } from './FormAutoComplete';
import type { ProjectMappingType } from '../../interfaces/ui/addProjectModal';
import type { Option } from '../../interfaces/ui/addProjectModal';
import {
  getTestPlanPlaceHolderText,
  getTestSuitePlaceHolderText,
} from './utils/helpers';

type FieldKey = 'testPlanName' | 'testSuiteName';

type FieldErrors = Partial<Record<FieldKey, string>>;
type FieldTouched = Partial<Record<FieldKey, boolean>>;

interface TestPlanAndSuiteProps {
  testPlanId: string;
  testSuiteId: string;
  projectMappingType: ProjectMappingType;
  mappedProjectId: string;

  testPlanOptions: Option[];
  testSuiteOptions: Option[];

  touched: FieldTouched;
  errors: FieldErrors;

  isEditing: boolean;
  loadingTestPlans: boolean;
  loadingTestSuites: boolean;
  required: boolean;
  existing?: boolean;
  onTestPlanChange: (value: string) => void;
  onTestSuiteChange: (value: string) => void;
  onFieldBlur: (event: React.FocusEvent<any>) => void;
}

type CopyGroup = {
  planLabel: string;
  suiteLabel: string;
  planError: string;
  suiteError: string;
};

const getCopy = (
  projectMappingType: ProjectMappingType,
  required: boolean,
): CopyGroup => {
  const isJira = projectMappingType === 'JIRA';

  if (isJira) {
    return required
      ? {
          planLabel: 'Folder for AI Generated Testcases',
          suiteLabel: "Sub-Folder for AI Generated TC's",
          planError: 'Select a folder',
          suiteError: 'Select a sub-folder',
        }
      : {
          planLabel: 'Existing Folder',
          suiteLabel: 'Existing Sub-Folder',
          planError: 'Select a folder',
          suiteError: 'Select a sub-folder',
        };
  }

  return required
    ? {
        planLabel: 'Test Plan for AI Generated Testcases',
        suiteLabel: 'Test Suite for AI Generated Testcases',
        planError: 'Select a test plan',
        suiteError: 'Select a test suite',
      }
    : {
        planLabel: 'Existing Test Plan',
        suiteLabel: 'Existing Test Suite',
        planError: 'Select a test plan',
        suiteError: 'Select a test suite',
      };
};

export default function TestPlanAndSuite({
  testPlanId,
  testSuiteId,
  projectMappingType,
  mappedProjectId,
  testPlanOptions,
  testSuiteOptions,
  touched,
  errors,
  isEditing,
  loadingTestPlans,
  loadingTestSuites,
  required,
  existing = false,
  onTestPlanChange,
  onTestSuiteChange,
  onFieldBlur,
}: TestPlanAndSuiteProps) {
  const copy = getCopy(projectMappingType, required && !existing);
  const isJira = projectMappingType === 'JIRA';
  const fields = [
    {
      id: 'testPlanName' as const,
      label: copy.planLabel,
      value: testPlanId,
      placeholder: getTestPlanPlaceHolderText(
        mappedProjectId,
        loadingTestPlans,
        testPlanOptions.length,
        isJira,
      ),
      options: testPlanOptions,
      touched: touched.testPlanName,
      error: errors.testPlanName,
      onChange: onTestPlanChange,
      disabled: isEditing || !mappedProjectId || loadingTestPlans,
      loading: loadingTestPlans,
      hasNoData:
        !isEditing &&
        !!mappedProjectId &&
        !loadingTestPlans &&
        testPlanOptions.length === 0,
      hierarchicalIndent: false,
      helperError: copy.planError,
    },
    {
      id: 'testSuiteName' as const,
      label: copy.suiteLabel,
      value: testSuiteId,
      placeholder: getTestSuitePlaceHolderText(
        testPlanId,
        loadingTestSuites,
        testSuiteOptions.length,
        isJira,
      ),
      options: testSuiteOptions,
      touched: touched.testSuiteName,
      error: errors.testSuiteName,
      onChange: onTestSuiteChange,
      disabled: isEditing || !testPlanId || loadingTestSuites,
      loading: loadingTestSuites,
      hasNoData:
        !isEditing &&
        !!testPlanId &&
        !loadingTestSuites &&
        testSuiteOptions.length === 0,
      hierarchicalIndent: true,
      helperError: copy.suiteError,
    },
  ];

  return (
    <>
      {fields.map(field => (
        <FormAutoComplete
          key={field.id}
          id={field.id}
          label={field.label}
          value={field.value}
          onChange={field.onChange}
          onBlur={onFieldBlur}
          options={field.options}
          error={
            field.touched && field.options.length > 0 && field.error
              ? field.helperError
              : undefined
          }
          placeholder={field.placeholder}
          disabled={field.disabled}
          required={required}
          loading={field.loading}
          hasNoData={field.hasNoData}
          hierarchicalIndent={field.hierarchicalIndent}
          indentSizePx={20}
        />
      ))}
    </>
  );
}
